// `excel_insert_model` (sculpt-api `InsertModel`, `sculpt/templates`): a new project-finance model, filled on the
// server from a template, inserted into an EMPTY workbook. The insertion deletes the sheets that were there, so it
// is refused the moment anything on them could be lost.
// Pure helpers — the executor reads the workbook and runs Office.js, this decides and shapes.
import type { CellValue, FilledTemplate, InsertModelInput, InsertModelOutput, ToolError } from '../../types/excel';
import { isValidAddress, splitSheetPrefix } from './a1';

/** `Workbook.insertWorksheetsFromBase64`. */
export const INSERT_MODEL_API = '1.13';

/** Prefix of the names the empty sheets take while the model goes in, when one of them holds a name the model
 *  brings (`pcp_` namespace: everything the add-in writes into a workbook). They are deleted in the same run. */
export const PLACEHOLDER_PREFIX = 'pcp_empty_';

/** What the executor found on one sheet. */
export interface SheetContent {
  name: string;
  /** `getUsedRangeOrNullObject(true)` was not null: the sheet holds a value or a formula. */
  hasValues: boolean;
  /** Sheet-scoped names. */
  names: number;
  charts: number;
  /** Pictures, shapes, text boxes; null when the host cannot count them. */
  shapes: number | null;
}

export interface WorkbookContent {
  sheets: SheetContent[];
  /** Workbook-scoped names. */
  names: number;
}

/** Why the workbook is not empty, one short phrase per finding; [] when it is. Deleting its sheets must lose
 *  nothing: values and formulas, names (workbook or sheet scope), charts and shapes all count. */
export function nonEmptyReasons(content: WorkbookContent): string[] {
  const reasons: string[] = [];
  const withValues = content.sheets.filter((s) => s.hasValues).map((s) => s.name);
  if (withValues.length) reasons.push(`data on ${listed(withValues)}`);
  const names = content.names + content.sheets.reduce((n, s) => n + s.names, 0);
  if (names) reasons.push(`${names} defined name${names > 1 ? 's' : ''}`);
  const charts = content.sheets.filter((s) => s.charts > 0).map((s) => s.name);
  if (charts.length) reasons.push(`charts on ${listed(charts)}`);
  const shapes = content.sheets.filter((s) => (s.shapes ?? 0) > 0).map((s) => s.name);
  if (shapes.length) reasons.push(`shapes on ${listed(shapes)}`);
  return reasons;
}

function listed(names: string[], max = 5): string {
  const head = names.slice(0, max).map((n) => `'${n}'`).join(', ');
  return names.length > max ? `${head} and ${names.length - max} more` : head;
}

export function workbookNotEmptyError(reasons: string[]): ToolError {
  return {
    error: `The workbook is not empty (${reasons.join('; ')}): a new model is only inserted into an empty workbook.`,
    code: 'WorkbookNotEmpty',
    retryable: false,
    hint: 'Ask the user to open an empty workbook and run the request there.',
  };
}

export function apiNotSupportedError(): ToolError {
  return {
    error: `This Excel version cannot insert a model (ExcelApi ${INSERT_MODEL_API} is required to insert sheets from a file).`,
    code: 'ApiNotSupported',
    retryable: false,
    hint: 'Tell the user this Excel version cannot insert a model; an up-to-date Excel for Windows or Mac can.',
  };
}

export function notConfiguredError(): ToolError {
  return { error: 'Inserting a model is not configured in this add-in build (no API client).', code: 'NotConfigured', retryable: false, hint: 'Tell the user a new model cannot be inserted here.' };
}

/** The problem of an input the server should never send, or null. Read back cells and checks are checked one by one
 *  later (an unreadable one reads null), so a typo in one does not cost the whole model. */
export function insertModelProblem(input: unknown): string | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return 'the input must be an object';
  const i = input as Record<string, unknown>;
  if (typeof i.template !== 'string' || !i.template.trim()) return 'template is required';
  if (typeof i.technology !== 'string' || !i.technology.trim()) return 'technology is required';
  if (i.inputs !== undefined && (typeof i.inputs !== 'object' || i.inputs === null || Array.isArray(i.inputs))) return 'inputs must be an object';
  if (!Array.isArray(i.sheets) || !i.sheets.length || i.sheets.some((s) => typeof s !== 'string' || !s)) return 'sheets must list the sheet names';
  if (i.read_back !== undefined && !Array.isArray(i.read_back)) return 'read_back must be a list';
  if (i.checks !== undefined && !Array.isArray(i.checks)) return 'checks must be a list';
  return null;
}

/** A `Sheet!A1` reference split, or null when it is not one. */
export function parseCellRef(ref: unknown): { sheet: string; address: string } | null {
  if (typeof ref !== 'string') return null;
  const { sheet, address } = splitSheetPrefix(ref);
  if (!sheet || !address || !isValidAddress(address)) return null;
  return { sheet, address };
}

/** The renames that free the model's sheet names before the insertion: [current name, placeholder] for every empty
 *  sheet whose name (case-insensitive, as Excel compares them) the model brings. Excel would otherwise insert the
 *  model's sheet as "Inputs (2)" and every formula naming 'Inputs' would read the wrong sheet once it is deleted. */
export function placeholderRenames(existing: string[], incoming: string[]): [string, string][] {
  const wanted = new Set(incoming.map((n) => n.toLowerCase()));
  const taken = new Set([...existing, ...incoming].map((n) => n.toLowerCase()));
  const out: [string, string][] = [];
  let i = 1;
  for (const name of existing) {
    if (!wanted.has(name.toLowerCase())) continue;
    while (taken.has(`${PLACEHOLDER_PREFIX}${i}`.toLowerCase())) i++;
    const placeholder = `${PLACEHOLDER_PREFIX}${i}`;
    taken.add(placeholder.toLowerCase());
    out.push([name, placeholder]);
  }
  return out;
}

/** The problem of a fill response the add-in cannot insert, or null. */
export function filledTemplateProblem(filled: unknown): string | null {
  if (!filled || typeof filled !== 'object') return 'the server returned no file';
  const f = filled as Partial<FilledTemplate>;
  if (typeof f.file_base64 !== 'string' || !f.file_base64) return 'the server returned no file';
  return null;
}

export function invalidResponseError(problem: string): ToolError {
  return { error: `excel_insert_model: ${problem}`, code: 'InvalidResponse', retryable: true, hint: 'Retry once; if it recurs, tell the user the model could not be fetched.' };
}

/** The tool error of a failed `POST /agent-v2/templates/{template}/fill`. An HTTP error carries `status` and the
 *  response `body` (ApiError); anything else is the network. A 4xx will fail the same way again (the server's
 *  problems are in the message, so the model can fix the answers); a 5xx or the network may not. */
export function fillError(err: unknown, template: string): ToolError {
  const status = httpStatus(err);
  if (status === null) {
    const message = err instanceof Error ? err.message : String(err);
    return { error: `Could not reach the server to fetch the template: ${message}`, code: 'NetworkError', retryable: true, hint: 'Retry once; if it recurs, tell the user the server cannot be reached.' };
  }
  const detail = errorDetail(err);
  if (status === 404) {
    return { error: `Unknown template '${template}'${detail ? ` (${detail})` : ''}.`, code: 'TemplateNotFound', retryable: false, hint: 'Call model_template to read the templates there are; do not retry this one.' };
  }
  if (status === 422) {
    return { error: `The server refused the answers: ${detail || 'invalid inputs'}`, code: 'InvalidTemplateInputs', retryable: false, hint: 'Fix the answers the problems name (model_template shows each Assumption and its bounds), then call the tool again with them.' };
  }
  if (status >= 500) {
    return { error: `The server failed to fill the template (HTTP ${status})${detail ? `: ${detail}` : ''}`, code: 'ApiError', retryable: true, hint: 'Retry once; if it recurs, tell the user the model could not be built.' };
  }
  return { error: `The server refused to fill the template (HTTP ${status})${detail ? `: ${detail}` : ''}`, code: 'ApiError', retryable: false, hint: 'Tell the user the model could not be built.' };
}

function httpStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' && Number.isFinite(status) ? status : null;
}

/** The FastAPI `detail` of an error body as one line: `{detail: {problems: [...]}}` joins the problems,
 *  `{detail: "..."}` is the message, anything else is the raw text. */
function errorDetail(err: unknown): string {
  const body = (err as { body?: unknown }).body;
  if (typeof body !== 'string' || !body) return '';
  try {
    const detail = (JSON.parse(body) as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
    const problems = (detail as { problems?: unknown } | null)?.problems;
    if (Array.isArray(problems)) return problems.map((p) => (typeof p === 'string' ? p : JSON.stringify(p))).join('; ');
    return detail === undefined ? body : JSON.stringify(detail);
  } catch {
    return body;
  }
}

/** A check passes when its cell reads exactly 0; an error, a blank or any other number fails it. */
export function checksFailed(values: CellValue[]): number {
  return values.filter((v) => v !== 0).length;
}

/** The tool result: `readBack` and `checks` are the values of `input.read_back` / `input.checks`, in order (null
 *  for a cell that could not be read). */
export function insertModelResult(input: InsertModelInput, inserted: string[], activated: string, readBack: CellValue[], checks: CellValue[]): InsertModelOutput {
  return {
    status: 'ok',
    sheets: inserted,
    read_back: (input.read_back ?? []).map((entry, i) => ({ ...entry, value: readBack[i] ?? null })),
    checks_failed: checksFailed((input.checks ?? []).map((_, i) => checks[i] ?? null)),
    defaulted: input.defaulted ?? [],
    _changes: { sheet: activated, address: 'A1', cells: 0 },
  };
}
